﻿import { ShiftReportStats } from "@/pages/shift-report/models/ShiftReportResponse";
import {
	Box,
	Card,
	Table,
	TableBody,
	TableCell,
	TableRow,
	Tooltip,
} from "@mui/material";

type Props = {
	data: ShiftReportStats | null;
};

const formatNumber = (
	value: number | null | undefined,
	decimals = 0,
): string => {
	if (value === undefined || value === null) return "-";
	return Number(value.toFixed(decimals)).toLocaleString("en-US", {
		minimumFractionDigits: decimals,
		maximumFractionDigits: decimals,
	});
};

export const M1ProductionStats = ({ data }: Props) => {
	if (!data) return null;

	const productionRows = [
		{
			label: "Billet Tons Charged",
			value: "-",
			tooltip: "Total weight of billets charged — not yet tracked",
		},
		{
			label: "Billet Quantity",
			value: formatNumber(data.BilletPcs),
			tooltip: "Total billets charged",
		},
		{
			label: "Rolled Tons",
			value: formatNumber(data.RolledTons, 2),
			tooltip: "Weight of successfully rolled billets",
		},
		{
			label: "Finished Tons",
			value: formatNumber(data.FinishedTons),
			tooltip: "Weight of finished product",
		},
		{
			label: "Mill Yield",
			value: "-",
			tooltip: "Not yet tracked",
		},
		{
			label: "Finished Yield",
			value: "-",
			tooltip: "Finished Tons / Billet Tons Charged x 100 — not yet tracked",
		},
	];

	// M1: No TotalAdjustments row
	const performanceRows = [
		{
			label: "Total Cobbles",
			value: "-",
			tooltip: "Cobble events in shift — not yet tracked",
		},
		{
			label: "Production",
			value:
				data.Production != null ? `${formatNumber(data.Production, 2)}%` : "-",
			indicator: "production",
			tooltip: "% of shift time spent producing",
		},
		{
			label: "Delays",
			value: data.Delays != null ? `${formatNumber(data.Delays, 2)}%` : "-",
			indicator: "delay",
			tooltip: "% of shift time in delays",
		},
		{
			label: "Gap Time",
			value: data.GapTime != null ? `${formatNumber(data.GapTime, 2)}%` : "-",
			indicator: "gaptime",
			tooltip: "% of shift time with no production and no delays",
		},
		{
			label: "Hours - Actual",
			value: formatNumber(data.HoursActual, 2),
			tooltip: "Elapsed hours since shift start",
		},
		{
			label: "TPH - Actual",
			value: formatNumber(data.TPH, 2),
			tooltip: "Rolled Tons / Hours Actual",
		},
		{
			label: "TPH - Targeted",
			value: formatNumber(data.TargetTPH, 2),
			tooltip: "Section catalog target TPH",
		},
	];

	return (
		<Box sx={{ display: "flex", flexDirection: "row", gap: 1 }}>
			<Card variant="outlined" sx={{ flex: 1, p: 1 }}>
				<Table
					size="small"
					sx={{ "& td, & th": { py: 0.2, px: 0.5, borderBottom: "none" } }}
				>
					<TableBody>
						{productionRows.map((row) => (
							<TableRow key={row.label}>
								<Tooltip title={row.tooltip} arrow placement="right">
									<TableCell sx={{ fontWeight: 500, fontSize: "0.75rem" }}>
										{row.label}
									</TableCell>
								</Tooltip>
								<TableCell align="right" sx={{ fontSize: "0.75rem" }}>
									{row.value}
								</TableCell>
							</TableRow>
						))}
					</TableBody>
				</Table>
			</Card>

			<Card variant="outlined" sx={{ flex: 1, p: 1 }}>
				<Table
					size="small"
					sx={{ "& td, & th": { py: 0.2, px: 0.5, borderBottom: "none" } }}
				>
					<TableBody>
						{performanceRows.map((row) => (
							<TableRow key={row.label}>
								<Tooltip title={row.tooltip} arrow placement="right">
									<TableCell sx={{ fontSize: "0.75rem" }}>
										{row.indicator && (
											<Box
												component="span"
												sx={{
													display: "inline-block",
													width: 8,
													height: 8,
													borderRadius: "50%",
													backgroundColor:
														row.indicator === "production"
															? "#4CAF50"
															: row.indicator === "delay"
																? "#F44336"
																: "#9E9E9E",
													mr: 0.5,
													verticalAlign: "middle",
												}}
											/>
										)}
										{row.label}
									</TableCell>
								</Tooltip>
								<TableCell align="right" sx={{ fontSize: "0.75rem" }}>
									{row.value}
								</TableCell>
							</TableRow>
						))}
					</TableBody>
				</Table>
			</Card>
		</Box>
	);
};
